type RequirementCategory = 'A' | 'B' | 'C' | 'core';

type ElectiveRequirementLike = {
  label?: string | null;
  requireCount?: number | null;
  categories?: RequirementCategory[] | null;
  include_subjects?: string[] | null;
  exclude_subjects?: string[] | null;
};

export const toSentenceCase = (label: string): string => {
  const normalized = label.toLocaleLowerCase();
  return normalized.length > 0
    ? normalized[0].toLocaleUpperCase() + normalized.slice(1)
    : normalized;
};

/**
 * Returns true if the requirement uses include_subjects (rendered as a "One of" list instead of a single row)
 */
export const isIncludeSubjectsRequirement = (requirement: ElectiveRequirementLike): boolean => {
  return Array.isArray(requirement.include_subjects) && requirement.include_subjects.length > 0;
};

export const formatGerElectiveRequirementLabel = (
  requirement: ElectiveRequirementLike,
): string => {
  if (requirement.label) {
    return toSentenceCase(requirement.label);
  }

  const count = Number.isFinite(requirement.requireCount) && (requirement.requireCount ?? 0) > 0
    ? Number(requirement.requireCount)
    : 1;
  const rawCategories = requirement.categories;
  const categories: RequirementCategory[] = Array.isArray(rawCategories)
    ? rawCategories.filter((category): category is RequirementCategory =>
      category === 'A' || category === 'B' || category === 'C' || category === 'core')
    : (typeof rawCategories === 'string' && ['A', 'B', 'C', 'core'].includes(rawCategories))
      ? [rawCategories as RequirementCategory]
      : [];
  const excludeSubjects = Array.isArray(requirement.exclude_subjects) ? requirement.exclude_subjects : [];

  const baseLabel = count === 1
    ? 'Any one subject'
    : count === 2
      ? 'Any two subjects'
      : `Any ${count} subjects`;

  const displayCategories = categories.filter(category => category !== 'core');

  let label = baseLabel;
  if (displayCategories.length > 0 && displayCategories.length < 3) {
    label += ` (in Cat. ${displayCategories.join(', ')})`;
  }

  if (excludeSubjects.length > 0) {
    label += ` exclude ${excludeSubjects.join(', ')}`;
  }

  return label;
};

export const formatElectiveRequirementLabel = (requirement: ElectiveRequirementLike): string => {
  return requirement.label
    ? toSentenceCase(requirement.label)
    : formatGerElectiveRequirementLabel(requirement);
};
